import React, { useState, useEffect } from 'react';
import {
  Flex,
  Button,
  Tooltip,
  TooltipTrigger,
  ActionButton,
  ProgressCircle,
} from '@adobe/react-spectrum';
import moment from 'moment';
import PropTypes from 'prop-types';

import lastUpdated from '../../utils/lastUpdated';
import actionWebInvoke from '../../utils/actionCallUtils';
import actions from '../../config.json';
import OBJCODE from '../../constants';
import {
  Container,
  Header,
  DateText,
  SubHeader,
  RequestLayout,
  Left,
  CircleStatus,
  RequestDescription,
  Right,
} from './styles';

const RequestProgress = ({ user }) => {
  const [state, setState] = useState({ isLoading: true, elements: [] });
  const [requestsToShow, setRequestsToShow] = useState([]);
  const [showMore, setShowMore] = useState(false);

  const showNumberOfRequests = ({ elements }) => {
    return showMore
      ? setRequestsToShow(elements)
      : setRequestsToShow(elements.slice(0, 5));
  };

  async function getExistingRequestList() {
    setState({ ...state, isLoading: true });
    const fields = ['name', 'ID', 'entryDate', 'status'];
    const queryParams = { ownerID: user };
    const actionResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.ISSUE, queryParams, fields },
    );
    setState({ ...state, elements: actionResponse, isLoading: false });
  }

  useEffect(() => {
    getExistingRequestList();
  }, [user]);

  useEffect(() => {
    showNumberOfRequests(state);
  }, [state, showMore]);

  const fixDateToParse = date => {
    return date.replace(/:([^:]*)$/, '.' + '$1');
  };

  const formatDateToShow = date => {
    return moment(new Date(fixDateToParse(date))).format('MM/DD/YY');
    // return moment(new Date(fixDateToParse(date))).format('MM/DD/YY, HH:MM A');
  };

  requestsToShow.sort((a, b) => {
    const incomingDateA = fixDateToParse(a.entryDate);
    const incomingDateB = fixDateToParse(b.entryDate);
    return moment(new Date(incomingDateB)) - moment(new Date(incomingDateA));
  });

  const handleClick = (url, id) => {
    return window.open(`${url}/issue/${id}/overview`, '_blank');
  };

  const circleColor = status => {
    switch (status) {
      case 'NEW':
        return { color: '#308fea', text: 'New' };
      case 'INP':
        return { color: '#44b556', text: 'In Progress' };
      case 'AWF':
        return { color: '#e0be39', text: 'Awaiting Feedback' };
      case 'OHD':
        return { color: '#e34850', text: 'On Hold' };
      case 'WRS':
        return { color: '#46dbff', text: "Won't resolve" };
      default:
        return { color: '#1b959a', text: 'Closed' };
    }
  };

  return (
    <Container direction="row">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        marginBottom="size-160"
      >
        <Header>Requests</Header>
        <DateText>{lastUpdated()}</DateText>
      </Flex>
      <SubHeader>
        <span>Path</span>
        <span>Entry Date</span>
      </SubHeader>
      <RequestLayout>
        {requestsToShow.length !== 0 ? (
          requestsToShow.map(el => (
            <>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                marginY="size-175"
                key={el.ID}
              >
                <Left>
                  <CircleStatus color={circleColor(el.status).color} />
                  <RequestDescription>
                    <TooltipTrigger offset={-10}>
                      <ActionButton
                        onClick={() => handleClick(el.workfrontEndpoint, el.ID)}
                      >
                        <span>{el.name}</span>
                      </ActionButton>
                      <Tooltip>{el.name}</Tooltip>
                    </TooltipTrigger>
                  </RequestDescription>
                </Left>
                <Right>
                  <i>{circleColor(el.status).text}</i>
                  <span>{formatDateToShow(el.entryDate)}</span>
                </Right>
              </Flex>
            </>
          ))
        ) : (
          <Flex justifyContent="center" marginY="size-1000">
            <ProgressCircle aria-label="Loading…" size="L" isIndeterminate />
          </Flex>
        )}
      </RequestLayout>

      {requestsToShow.length > 4 && (
        <Button
          variant="secondary"
          marginTop="size-300"
          marginBottom="size-0"
          marginX="size-0"
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? 'View less' : 'View more'}
        </Button>
      )}
    </Container>
  );
};

RequestProgress.propTypes = {
  user: PropTypes.string,
};

RequestProgress.defaultProps = {
  user: '',
};

export default RequestProgress;
